import React, { useEffect, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import { DataGrid, GridCellParams } from '@mui/x-data-grid';
import { ConfigurationHeaders } from './Configuration.header';
import {
  useGetAccessTokenExpirationDate,
  useGetConfigs,
  useUpdateConfigs,
  usePostRenewAccessToken,
} from 'src/api/hooks/useConfigs';
import { ConfigEditModal } from './ConfigUpdateModal';
import { PlusOne } from '@mui/icons-material';
import { Config, configValueLabels, cronJobLabels } from 'src/api/hooks/useConfigs.types';
import { AccessTokenInfo, OnFbSuccess } from 'src/pages/Configuration/Configuration.types';

export const ConfigurationScreen: React.FC = () => {
  const [configs, setConfigs] = useState<Config[]>([]);
  const [selectedRow, setSelectedRow] = useState<Config>();
  const { t } = useTranslation();
  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const [accessToken, setAccessTokenInfo] = useState<AccessTokenInfo>({
    token: '',
    expiresAt: '',
    shouldRenew: false,
  });

  const [getConfigValues, { loading }] = useGetConfigs({
    onComplete: res => res && setConfigs(res),
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('alerts.configScreen.error'),
      });
    },
  });

  const [getAccessTokenExpiration] = useGetAccessTokenExpirationDate({
    onComplete: res => {
      if (res) {
        setAccessTokenInfo({
          token: res.currentToken,
          expiresAt: res.accessTokenExpirationDate,
          shouldRenew: res.aboutToExpire,
        });
      }
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('alerts.configScreen.facebook.getAccessTokenExpirationDate.error'),
      });
    },
  });

  const [renewAccessToken, { loading: renewAccessTokenLoading }] = usePostRenewAccessToken({
    onComplete: res => {
      if (res) {
        setAccessTokenInfo(prev => ({
          token: res,
          expiresAt: prev.expiresAt,
          shouldRenew: prev.shouldRenew,
        }));
      }
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: t('alerts.configScreen.facebook.renewAccessToken.success'),
      });
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('alerts.configScreen.facebook.renewAccessToken.error'),
      });
    },
  });

  const [updateConfig, { loading: isUpdateLoading }] = useUpdateConfigs({
    onComplete: res => {
      if (res) {
        setConfigs(res);
        setPopUpData({
          isOpen: true,
          type: PopUpEnum.success,
          message: t('alerts.configScreen.success'),
        });
      }
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('alerts.configScreen.error'),
      });
    },
  });

  const handleConfigEdit = (editedConfig: Config) => {
    updateConfig({ variables: editedConfig });
  };

  const handleNewConfig = () => {
    setSelectedRow({
      id: -1,
      name: '',
      value: '',
    });
  };

  useEffect(() => {
    getAccessTokenExpiration();
    getConfigValues();
  }, [accessToken.token]);

  const handleCellClick = (params: GridCellParams<Config>) => {
    const { row } = params;

    const isCronStatus =
      Object.values(cronJobLabels).includes(row.name) ||
      Object.values(configValueLabels).includes(row.name);

    if (params.field === 'config.value' && !isCronStatus) {
      setSelectedRow(row as Config);
    }
  };

  const onFbSuccess = (response: OnFbSuccess) => {
    renewAccessToken({ variables: { shortLivedToken: response.accessToken } });
  };

  return (
    <Grid style={{ width: '100%' }}>
      <DataGrid
        autoHeight
        editMode="cell"
        sx={{
          '.MuiDataGrid-cell': { color: 'white', whiteSpace: 'normal', wordWrap: 'break-word' },
        }}
        rowHeight={250}
        components={{
          Toolbar: () => (
            <Grid item xs={2} mb={2} style={{ padding: 20 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleNewConfig}
                sx={{ width: 250 }}
                startIcon={<PlusOne />}>
                {t('Add new config')}
              </Button>
            </Grid>
          ),
        }}
        autoPageSize
        rows={configs}
        columns={ConfigurationHeaders(
          t,
          handleConfigEdit,
          accessToken,
          onFbSuccess,
          renewAccessTokenLoading,
        )}
        disableSelectionOnClick={true}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        loading={loading || isUpdateLoading}
        pageSize={100}
        rowsPerPageOptions={[5, 20]}
        onCellClick={params => handleCellClick(params)}
      />
      {selectedRow && (
        <ConfigEditModal
          {...selectedRow}
          open={!!selectedRow}
          handleClose={() => setSelectedRow(undefined)}
          handleConfigEdit={handleConfigEdit}
        />
      )}
    </Grid>
  );
};
